import React, { useState, useEffect } from 'react';
import './LandingPage.css';

const LandingPage = ({ onLogin, onRegister }) => {
  const [showLoginForm, setShowLoginForm] = useState(false);
  const [showRegisterForm, setShowRegisterForm] = useState(false);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [email, setEmail] = useState('');

  // Handle ESC key to close forms
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        if (showLoginForm || showRegisterForm) {
          setShowLoginForm(false);
          setShowRegisterForm(false);
          setUsername('');
          setPassword('');
          setEmail('');
        }
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [showLoginForm, showRegisterForm]);

  const handleLogin = (e) => {
    e.preventDefault();
    onLogin(username, password);
  };

  const handleRegister = (e) => {
    e.preventDefault();
    onRegister(username, email, password);
  };

  return (
    <div className="landing-page">
      {/* Animated background elements */}
      <div className="background-animation">
        <div className="floating-table table-1">
          <div className="cell"></div>
          <div className="cell"></div>
          <div className="cell"></div>
          <div className="cell"></div>
        </div>
        <div className="floating-table table-2">
          <div className="cell"></div>
          <div className="cell"></div>
          <div className="cell"></div>
          <div className="cell"></div>
        </div>
        <div className="floating-table table-3">
          <div className="cell"></div>
          <div className="cell"></div>
          <div className="cell"></div>
          <div className="cell"></div>
        </div>

        {/* Floating chart elements */}
        <div className="floating-chart chart-1">
          <div className="bar" style={{ height: '60%' }}></div>
          <div className="bar" style={{ height: '80%' }}></div>
          <div className="bar" style={{ height: '40%' }}></div>
        </div>
        <div className="floating-chart chart-2">
          <div className="bar" style={{ height: '50%' }}></div>
          <div className="bar" style={{ height: '70%' }}></div>
          <div className="bar" style={{ height: '90%' }}></div>
        </div>

        {/* Geometric shapes */}
        <div className="geometric-shape shape-1"></div>
        <div className="geometric-shape shape-2"></div>
        <div className="geometric-shape shape-3"></div>
      </div>

      {/* Main content */}
      <div className="landing-content">
        <div className="hero-section">
          <h1 className="hero-title">
            <span className="gradient-text">Organized Tables</span>
          </h1>
          <p className="hero-subtitle">Build, organize, and manage your data, your company, your life, your whatever... beautifully</p>
          <p className="hero-subtitle secondary">Share tables (or not) with colleagues, friends, family, business partners so you are both truly aligned and literally on the same page.</p>
          <p className="hero-subtitle secondary">From small todo style lists to complex planning and project management</p>
          <p className="hero-subtitle secondary">Capture and view data the way <em>you</em> 🫵 want to see it</p>

          {!showLoginForm && !showRegisterForm && (
            <div className="cta-buttons">
              <button
                className="cta-button primary"
                onClick={() => setShowRegisterForm(true)}
              >
                Get Started
              </button>
              <button
                className="cta-button secondary"
                onClick={() => setShowLoginForm(true)}
              >
                Sign In
              </button>
            </div>
          )}

          {/* Login Form */}
          {showLoginForm && (
            <div className="auth-form">
              <h2>Welcome Back</h2>
              <form onSubmit={handleLogin}>
                <input
                  type="text"
                  placeholder="Username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  required
                />
                <input
                  type="password"
                  placeholder="Password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
                <button type="submit" className="auth-submit">Sign In</button>
                <button
                  type="button"
                  className="auth-cancel"
                  onClick={() => {
                    setShowLoginForm(false);
                    setUsername('');
                    setPassword('');
                  }}
                >
                  Cancel
                </button>
              </form>
              <p className="auth-switch">
                Don't have an account?{' '}
                <span
                  onClick={() => {
                    setShowLoginForm(false);
                    setShowRegisterForm(true);
                  }}
                >
                  Sign up
                </span>
              </p>
            </div>
          )}

          {/* Register Form */}
          {showRegisterForm && (
            <div className="auth-form">
              <h2>Create Account</h2>
              <form onSubmit={handleRegister}>
                <input
                  type="text"
                  placeholder="Username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  required
                />
                <input
                  type="email"
                  placeholder="Email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
                <input
                  type="password"
                  placeholder="Password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
                <button type="submit" className="auth-submit">Create Account</button>
                <button
                  type="button"
                  className="auth-cancel"
                  onClick={() => {
                    setShowRegisterForm(false);
                    setUsername('');
                    setEmail('');
                    setPassword('');
                  }}
                >
                  Cancel
                </button>
              </form>
              <p className="auth-switch">
                Already have an account?{' '}
                <span
                  onClick={() => {
                    setShowRegisterForm(false);
                    setShowLoginForm(true);
                  }}
                >
                  Sign in
                </span>
              </p>
            </div>
          )}
        </div>

        {/* Features Section */}
        {!showLoginForm && !showRegisterForm && (
          <div className="features-section">
            <div className="feature">
              <div className="feature-icon" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px' }}>
                <div style={{ display: 'flex', gap: '20px' }}>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px' }}>
                    <div style={{ fontSize: '36px' }}>📁</div>
                    <svg width="32" height="40" viewBox="0 0 32 40" fill="none" xmlns="http://www.w3.org/2000/svg">
                      {/* First row */}
                      <rect x="1" y="1" width="30" height="7" rx="1.5" fill="currentColor" opacity="0.3"/>
                      <rect x="2" y="2" width="6" height="5" rx="0.8" fill="white" opacity="0.9"/>
                      <rect x="9" y="2" width="6" height="5" rx="0.8" fill="white" opacity="0.9"/>
                      <rect x="16" y="2" width="6" height="5" rx="0.8" fill="white" opacity="0.9"/>
                      <rect x="23" y="2" width="6" height="5" rx="0.8" fill="white" opacity="0.9"/>

                      {/* Second row */}
                      <rect x="1" y="8" width="30" height="7" rx="1.5" fill="currentColor" opacity="0.3"/>
                      <rect x="2" y="9" width="6" height="5" rx="0.8" fill="white" opacity="0.9"/>
                      <rect x="9" y="9" width="6" height="5" rx="0.8" fill="white" opacity="0.9"/>
                      <rect x="16" y="9" width="6" height="5" rx="0.8" fill="white" opacity="0.9"/>
                      <rect x="23" y="9" width="6" height="5" rx="0.8" fill="white" opacity="0.9"/>

                      {/* Third row */}
                      <rect x="1" y="15" width="30" height="7" rx="1.5" fill="currentColor" opacity="0.3"/>
                      <rect x="2" y="16" width="6" height="5" rx="0.8" fill="white" opacity="0.9"/>
                      <rect x="9" y="16" width="6" height="5" rx="0.8" fill="white" opacity="0.9"/>
                      <rect x="16" y="16" width="6" height="5" rx="0.8" fill="white" opacity="0.9"/>
                      <rect x="23" y="16" width="6" height="5" rx="0.8" fill="white" opacity="0.9"/>
                    </svg>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px' }}>
                    <div style={{ fontSize: '36px' }}>📁</div>
                    <svg width="32" height="40" viewBox="0 0 32 40" fill="none" xmlns="http://www.w3.org/2000/svg">
                      {/* First row */}
                      <rect x="1" y="1" width="30" height="7" rx="1.5" fill="currentColor" opacity="0.3"/>
                      <rect x="2" y="2" width="6" height="5" rx="0.8" fill="white" opacity="0.9"/>
                      <rect x="9" y="2" width="6" height="5" rx="0.8" fill="white" opacity="0.9"/>
                      <rect x="16" y="2" width="6" height="5" rx="0.8" fill="white" opacity="0.9"/>
                      <rect x="23" y="2" width="6" height="5" rx="0.8" fill="white" opacity="0.9"/>

                      {/* Second row */}
                      <rect x="1" y="8" width="30" height="7" rx="1.5" fill="currentColor" opacity="0.3"/>
                      <rect x="2" y="9" width="6" height="5" rx="0.8" fill="white" opacity="0.9"/>
                      <rect x="9" y="9" width="6" height="5" rx="0.8" fill="white" opacity="0.9"/>
                      <rect x="16" y="9" width="6" height="5" rx="0.8" fill="white" opacity="0.9"/>
                      <rect x="23" y="9" width="6" height="5" rx="0.8" fill="white" opacity="0.9"/>
                    </svg>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px' }}>
                    <div style={{ fontSize: '36px' }}>📁</div>
                    <svg width="32" height="70" viewBox="0 0 32 70" fill="none" xmlns="http://www.w3.org/2000/svg">
                      {/* Table 1 - 2 rows */}
                      <rect x="1" y="1" width="30" height="7" rx="1.5" fill="currentColor" opacity="0.3"/>
                      <rect x="2" y="2" width="6" height="5" rx="0.8" fill="white" opacity="0.9"/>
                      <rect x="9" y="2" width="6" height="5" rx="0.8" fill="white" opacity="0.9"/>
                      <rect x="16" y="2" width="6" height="5" rx="0.8" fill="white" opacity="0.9"/>
                      <rect x="23" y="2" width="6" height="5" rx="0.8" fill="white" opacity="0.9"/>

                      <rect x="1" y="8" width="30" height="7" rx="1.5" fill="currentColor" opacity="0.3"/>
                      <rect x="2" y="9" width="6" height="5" rx="0.8" fill="white" opacity="0.9"/>
                      <rect x="9" y="9" width="6" height="5" rx="0.8" fill="white" opacity="0.9"/>
                      <rect x="16" y="9" width="6" height="5" rx="0.8" fill="white" opacity="0.9"/>
                      <rect x="23" y="9" width="6" height="5" rx="0.8" fill="white" opacity="0.9"/>

                      {/* Table 2 - 3 rows */}
                      <rect x="1" y="19" width="30" height="7" rx="1.5" fill="currentColor" opacity="0.3"/>
                      <rect x="2" y="20" width="6" height="5" rx="0.8" fill="white" opacity="0.9"/>
                      <rect x="9" y="20" width="6" height="5" rx="0.8" fill="white" opacity="0.9"/>
                      <rect x="16" y="20" width="6" height="5" rx="0.8" fill="white" opacity="0.9"/>
                      <rect x="23" y="20" width="6" height="5" rx="0.8" fill="white" opacity="0.9"/>

                      <rect x="1" y="26" width="30" height="7" rx="1.5" fill="currentColor" opacity="0.3"/>
                      <rect x="2" y="27" width="6" height="5" rx="0.8" fill="white" opacity="0.9"/>
                      <rect x="9" y="27" width="6" height="5" rx="0.8" fill="white" opacity="0.9"/>
                      <rect x="16" y="27" width="6" height="5" rx="0.8" fill="white" opacity="0.9"/>
                      <rect x="23" y="27" width="6" height="5" rx="0.8" fill="white" opacity="0.9"/>

                      <rect x="1" y="33" width="30" height="7" rx="1.5" fill="currentColor" opacity="0.3"/>
                      <rect x="2" y="34" width="6" height="5" rx="0.8" fill="white" opacity="0.9"/>
                      <rect x="9" y="34" width="6" height="5" rx="0.8" fill="white" opacity="0.9"/>
                      <rect x="16" y="34" width="6" height="5" rx="0.8" fill="white" opacity="0.9"/>
                      <rect x="23" y="34" width="6" height="5" rx="0.8" fill="white" opacity="0.9"/>

                      {/* Table 3 - 1 row */}
                      <rect x="1" y="44" width="30" height="7" rx="1.5" fill="currentColor" opacity="0.3"/>
                      <rect x="2" y="45" width="6" height="5" rx="0.8" fill="white" opacity="0.9"/>
                      <rect x="9" y="45" width="6" height="5" rx="0.8" fill="white" opacity="0.9"/>
                      <rect x="16" y="45" width="6" height="5" rx="0.8" fill="white" opacity="0.9"/>
                      <rect x="23" y="45" width="6" height="5" rx="0.8" fill="white" opacity="0.9"/>
                    </svg>
                  </div>
                </div>
              </div>
              <h3>Multi-file Workspaces</h3>
              <p>Organize your work across multiple files</p>
            </div>
            <div className="feature">
              <div className="feature-icon">
                <svg width="64" height="64" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
                  {/* Top row - parent */}
                  <rect x="6" y="8" width="54" height="9" rx="2.5" fill="currentColor" opacity="0.3"/>
                  {/* Cell boxes */}
                  <rect x="8" y="10" width="11" height="5" rx="1.2" fill="white" opacity="0.9"/>
                  <rect x="21" y="10" width="11" height="5" rx="1.2" fill="white" opacity="0.9"/>
                  <rect x="34" y="10" width="11" height="5" rx="1.2" fill="white" opacity="0.9"/>
                  <rect x="47" y="10" width="11" height="5" rx="1.2" fill="white" opacity="0.9"/>

                  {/* First indented row - child */}
                  <rect x="13" y="21" width="42" height="9" rx="2.5" fill="currentColor" opacity="0.3"/>
                  {/* Cell boxes */}
                  <rect x="16" y="23" width="11" height="5" rx="1.2" fill="white" opacity="0.8"/>
                  <rect x="29" y="23" width="11" height="5" rx="1.2" fill="white" opacity="0.8"/>
                  <rect x="42" y="23" width="11" height="5" rx="1.2" fill="white" opacity="0.8"/>

                  {/* Second indented row - child (adjacent) */}
                  <rect x="13" y="30" width="42" height="9" rx="2.5" fill="currentColor" opacity="0.3"/>
                  {/* Cell boxes */}
                  <rect x="16" y="32" width="11" height="5" rx="1.2" fill="white" opacity="0.8"/>
                  <rect x="29" y="32" width="11" height="5" rx="1.2" fill="white" opacity="0.8"/>
                  <rect x="42" y="32" width="11" height="5" rx="1.2" fill="white" opacity="0.8"/>

                  {/* Bottom row - parent */}
                  <rect x="6" y="43" width="54" height="9" rx="2.5" fill="currentColor" opacity="0.3"/>
                  {/* Cell boxes */}
                  <rect x="8" y="45" width="11" height="5" rx="1.2" fill="white" opacity="0.9"/>
                  <rect x="21" y="45" width="11" height="5" rx="1.2" fill="white" opacity="0.9"/>
                  <rect x="34" y="45" width="11" height="5" rx="1.2" fill="white" opacity="0.9"/>
                  <rect x="47" y="45" width="11" height="5" rx="1.2" fill="white" opacity="0.9"/>

                  {/* Connector line from parent to children */}
                  <path d="M 11 17 L 11 26 L 13 26" stroke="currentColor" strokeWidth="2.5" fill="none" opacity="0.5"/>
                </svg>
              </div>
              <h3>Nested Tables</h3>
              <p>Create complex data structures with ease</p>
            </div>
            <div className="feature">
              <div className="feature-icon" style={{ position: 'relative', overflow: 'visible' }}>
                <svg width="64" height="64" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ position: 'relative', zIndex: 10 }}>
                  {/* Nested table icon on top of the hand emoji */}
                  {/* Top row - parent */}
                  <rect x="6" y="8" width="54" height="9" rx="2.5" fill="currentColor" opacity="0.3"/>
                  {/* Cell boxes */}
                  <rect x="8" y="10" width="11" height="5" rx="1.2" fill="white" opacity="0.9"/>
                  <rect x="21" y="10" width="11" height="5" rx="1.2" fill="white" opacity="0.9"/>
                  <rect x="34" y="10" width="11" height="5" rx="1.2" fill="white" opacity="0.9"/>
                  <rect x="47" y="10" width="11" height="5" rx="1.2" fill="white" opacity="0.9"/>

                  {/* First indented row - child */}
                  <rect x="13" y="21" width="42" height="9" rx="2.5" fill="currentColor" opacity="0.3"/>
                  <rect x="16" y="23" width="11" height="5" rx="1.2" fill="white" opacity="0.8"/>
                  <rect x="29" y="23" width="11" height="5" rx="1.2" fill="white" opacity="0.8"/>
                  <rect x="42" y="23" width="11" height="5" rx="1.2" fill="white" opacity="0.8"/>

                  {/* Second indented row - child (adjacent) */}
                  <rect x="13" y="30" width="42" height="9" rx="2.5" fill="currentColor" opacity="0.3"/>
                  <rect x="16" y="32" width="11" height="5" rx="1.2" fill="white" opacity="0.8"/>
                  <rect x="29" y="32" width="11" height="5" rx="1.2" fill="white" opacity="0.8"/>
                  <rect x="42" y="32" width="11" height="5" rx="1.2" fill="white" opacity="0.8"/>

                  {/* Bottom row - parent */}
                  <rect x="6" y="43" width="54" height="9" rx="2.5" fill="currentColor" opacity="0.3"/>
                  <rect x="8" y="45" width="11" height="5" rx="1.2" fill="white" opacity="0.9"/>
                  <rect x="21" y="45" width="11" height="5" rx="1.2" fill="white" opacity="0.9"/>
                  <rect x="34" y="45" width="11" height="5" rx="1.2" fill="white" opacity="0.9"/>
                  <rect x="47" y="45" width="11" height="5" rx="1.2" fill="white" opacity="0.9"/>

                  {/* Connector line */}
                  <path d="M 11 17 L 11 26 L 13 26" stroke="currentColor" strokeWidth="2.5" fill="none" opacity="0.5"/>
                </svg>
                <div style={{ fontSize: '64px', position: 'absolute', bottom: '-45px', left: '20px', zIndex: 0 }}>🫴</div>
                <div style={{ fontSize: '64px', position: 'absolute', bottom: '-45px', right: '20px', transform: 'scaleX(-1)', zIndex: 0 }}>🫴</div>
              </div>
              <h3 style={{ paddingTop: '20px' }}>Share Tables with Others</h3>
              <p>Collaborate with read-only or edit permissions</p>
            </div>
            <div className="feature">
              <div className="feature-icon" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}>
                {/* Tables on top */}
                <div style={{ display: 'flex', gap: '12px' }}>
                  <svg width="44" height="33" viewBox="0 0 44 33" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <rect x="1" y="1" width="38" height="8" rx="1.5" fill="currentColor" opacity="0.3"/>
                    <rect x="2" y="2" width="8" height="6" rx="0.8" fill="white" opacity="0.9"/>
                    <rect x="11" y="2" width="8" height="6" rx="0.8" fill="white" opacity="0.9"/>
                    <rect x="20" y="2" width="8" height="6" rx="0.8" fill="white" opacity="0.9"/>
                    <rect x="29" y="2" width="8" height="6" rx="0.8" fill="white" opacity="0.9"/>

                    <rect x="1" y="9" width="38" height="8" rx="1.5" fill="currentColor" opacity="0.3"/>
                    <rect x="2" y="10" width="8" height="6" rx="0.8" fill="white" opacity="0.9"/>
                    <rect x="11" y="10" width="8" height="6" rx="0.8" fill="white" opacity="0.9"/>
                    <rect x="20" y="10" width="8" height="6" rx="0.8" fill="white" opacity="0.9"/>
                    <rect x="29" y="10" width="8" height="6" rx="0.8" fill="white" opacity="0.9"/>

                    <rect x="1" y="17" width="38" height="8" rx="1.5" fill="currentColor" opacity="0.3"/>
                    <rect x="2" y="18" width="8" height="6" rx="0.8" fill="white" opacity="0.9"/>
                    <rect x="11" y="18" width="8" height="6" rx="0.8" fill="white" opacity="0.9"/>
                    <rect x="20" y="18" width="8" height="6" rx="0.8" fill="white" opacity="0.9"/>
                    <rect x="29" y="18" width="8" height="6" rx="0.8" fill="white" opacity="0.9"/>
                  </svg>
                  <svg width="44" height="24" viewBox="0 0 44 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <rect x="1" y="1" width="38" height="8" rx="1.5" fill="currentColor" opacity="0.3"/>
                    <rect x="2" y="2" width="8" height="6" rx="0.8" fill="white" opacity="0.9"/>
                    <rect x="11" y="2" width="8" height="6" rx="0.8" fill="white" opacity="0.9"/>
                    <rect x="20" y="2" width="8" height="6" rx="0.8" fill="white" opacity="0.9"/>
                    <rect x="29" y="2" width="8" height="6" rx="0.8" fill="white" opacity="0.9"/>

                    <rect x="1" y="9" width="38" height="8" rx="1.5" fill="currentColor" opacity="0.3"/>
                    <rect x="2" y="10" width="8" height="6" rx="0.8" fill="white" opacity="0.9"/>
                    <rect x="11" y="10" width="8" height="6" rx="0.8" fill="white" opacity="0.9"/>
                    <rect x="20" y="10" width="8" height="6" rx="0.8" fill="white" opacity="0.9"/>
                    <rect x="29" y="10" width="8" height="6" rx="0.8" fill="white" opacity="0.9"/>
                  </svg>
                </div>

                {/* Double arrow in the middle */}
                <div style={{ fontSize: '40px' }}>↕️</div>

                {/* Cloud storage icon underneath */}
                <div style={{ fontSize: '48px' }}>☁️</div>
              </div>
              <h3>Export & Import</h3>
              <p>Your data, your way - backup and restore anytime</p>
            </div>
            <div className="feature">
              <div className="feature-icon" style={{ display: 'flex', gap: '6px', justifyContent: 'center' }}>
                {/* Blue theme */}
                <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <rect x="4" y="6" width="42" height="7" rx="2" fill="#333333" opacity="0.8"/>
                  <rect x="6" y="7.5" width="8" height="4" rx="1" fill="#4A9EFF" stroke="#4A9EFF" strokeWidth="1"/>
                  <rect x="16" y="7.5" width="8" height="4" rx="1" fill="#4A9EFF" stroke="#4A9EFF" strokeWidth="1"/>
                  <rect x="26" y="7.5" width="8" height="4" rx="1" fill="#4A9EFF" stroke="#4A9EFF" strokeWidth="1"/>
                  <rect x="36" y="7.5" width="8" height="4" rx="1" fill="#4A9EFF" stroke="#4A9EFF" strokeWidth="1"/>
                  <rect x="10" y="16" width="32" height="7" rx="2" fill="#333333" opacity="0.8"/>
                  <rect x="12" y="17.5" width="8" height="4" rx="1" fill="#4A9EFF" stroke="#4A9EFF" strokeWidth="1"/>
                  <rect x="22" y="17.5" width="8" height="4" rx="1" fill="#4A9EFF" stroke="#4A9EFF" strokeWidth="1"/>
                  <rect x="32" y="17.5" width="8" height="4" rx="1" fill="#4A9EFF" stroke="#4A9EFF" strokeWidth="1"/>
                  <rect x="10" y="23" width="32" height="7" rx="2" fill="#333333" opacity="0.8"/>
                  <rect x="12" y="24.5" width="8" height="4" rx="1" fill="#4A9EFF" stroke="#4A9EFF" strokeWidth="1"/>
                  <rect x="22" y="24.5" width="8" height="4" rx="1" fill="#4A9EFF" stroke="#4A9EFF" strokeWidth="1"/>
                  <rect x="32" y="24.5" width="8" height="4" rx="1" fill="#4A9EFF" stroke="#4A9EFF" strokeWidth="1"/>
                  <rect x="4" y="33" width="42" height="7" rx="2" fill="#333333" opacity="0.8"/>
                  <rect x="6" y="34.5" width="8" height="4" rx="1" fill="#4A9EFF" stroke="#4A9EFF" strokeWidth="1"/>
                  <rect x="16" y="34.5" width="8" height="4" rx="1" fill="#4A9EFF" stroke="#4A9EFF" strokeWidth="1"/>
                  <rect x="26" y="34.5" width="8" height="4" rx="1" fill="#4A9EFF" stroke="#4A9EFF" strokeWidth="1"/>
                  <rect x="36" y="34.5" width="8" height="4" rx="1" fill="#4A9EFF" stroke="#4A9EFF" strokeWidth="1"/>
                  <path d="M 8 13 L 8 20 L 10 20" stroke="#333333" strokeWidth="2" fill="none" opacity="0.8"/>
                </svg>

                {/* Orange theme */}
                <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <rect x="4" y="6" width="42" height="7" rx="2" fill="#333333" opacity="0.8"/>
                  <rect x="6" y="7.5" width="8" height="4" rx="1" fill="#FF8C42" stroke="#FF8C42" strokeWidth="1"/>
                  <rect x="16" y="7.5" width="8" height="4" rx="1" fill="#FF8C42" stroke="#FF8C42" strokeWidth="1"/>
                  <rect x="26" y="7.5" width="8" height="4" rx="1" fill="#FF8C42" stroke="#FF8C42" strokeWidth="1"/>
                  <rect x="36" y="7.5" width="8" height="4" rx="1" fill="#FF8C42" stroke="#FF8C42" strokeWidth="1"/>
                  <rect x="10" y="16" width="32" height="7" rx="2" fill="#333333" opacity="0.8"/>
                  <rect x="12" y="17.5" width="8" height="4" rx="1" fill="#FF8C42" stroke="#FF8C42" strokeWidth="1"/>
                  <rect x="22" y="17.5" width="8" height="4" rx="1" fill="#FF8C42" stroke="#FF8C42" strokeWidth="1"/>
                  <rect x="32" y="17.5" width="8" height="4" rx="1" fill="#FF8C42" stroke="#FF8C42" strokeWidth="1"/>
                  <rect x="10" y="23" width="32" height="7" rx="2" fill="#333333" opacity="0.8"/>
                  <rect x="12" y="24.5" width="8" height="4" rx="1" fill="#FF8C42" stroke="#FF8C42" strokeWidth="1"/>
                  <rect x="22" y="24.5" width="8" height="4" rx="1" fill="#FF8C42" stroke="#FF8C42" strokeWidth="1"/>
                  <rect x="32" y="24.5" width="8" height="4" rx="1" fill="#FF8C42" stroke="#FF8C42" strokeWidth="1"/>
                  <rect x="4" y="33" width="42" height="7" rx="2" fill="#333333" opacity="0.8"/>
                  <rect x="6" y="34.5" width="8" height="4" rx="1" fill="#FF8C42" stroke="#FF8C42" strokeWidth="1"/>
                  <rect x="16" y="34.5" width="8" height="4" rx="1" fill="#FF8C42" stroke="#FF8C42" strokeWidth="1"/>
                  <rect x="26" y="34.5" width="8" height="4" rx="1" fill="#FF8C42" stroke="#FF8C42" strokeWidth="1"/>
                  <rect x="36" y="34.5" width="8" height="4" rx="1" fill="#FF8C42" stroke="#FF8C42" strokeWidth="1"/>
                  <path d="M 8 13 L 8 20 L 10 20" stroke="#333333" strokeWidth="2" fill="none" opacity="0.8"/>
                </svg>

                {/* Grey theme */}
                <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <rect x="4" y="6" width="42" height="7" rx="2" fill="#333333" opacity="0.8"/>
                  <rect x="6" y="7.5" width="8" height="4" rx="1" fill="#888888" stroke="#888888" strokeWidth="1"/>
                  <rect x="16" y="7.5" width="8" height="4" rx="1" fill="#888888" stroke="#888888" strokeWidth="1"/>
                  <rect x="26" y="7.5" width="8" height="4" rx="1" fill="#888888" stroke="#888888" strokeWidth="1"/>
                  <rect x="36" y="7.5" width="8" height="4" rx="1" fill="#888888" stroke="#888888" strokeWidth="1"/>
                  <rect x="10" y="16" width="32" height="7" rx="2" fill="#333333" opacity="0.8"/>
                  <rect x="12" y="17.5" width="8" height="4" rx="1" fill="#888888" stroke="#888888" strokeWidth="1"/>
                  <rect x="22" y="17.5" width="8" height="4" rx="1" fill="#888888" stroke="#888888" strokeWidth="1"/>
                  <rect x="32" y="17.5" width="8" height="4" rx="1" fill="#888888" stroke="#888888" strokeWidth="1"/>
                  <rect x="10" y="23" width="32" height="7" rx="2" fill="#333333" opacity="0.8"/>
                  <rect x="12" y="24.5" width="8" height="4" rx="1" fill="#888888" stroke="#888888" strokeWidth="1"/>
                  <rect x="22" y="24.5" width="8" height="4" rx="1" fill="#888888" stroke="#888888" strokeWidth="1"/>
                  <rect x="32" y="24.5" width="8" height="4" rx="1" fill="#888888" stroke="#888888" strokeWidth="1"/>
                  <rect x="4" y="33" width="42" height="7" rx="2" fill="#333333" opacity="0.8"/>
                  <rect x="6" y="34.5" width="8" height="4" rx="1" fill="#888888" stroke="#888888" strokeWidth="1"/>
                  <rect x="16" y="34.5" width="8" height="4" rx="1" fill="#888888" stroke="#888888" strokeWidth="1"/>
                  <rect x="26" y="34.5" width="8" height="4" rx="1" fill="#888888" stroke="#888888" strokeWidth="1"/>
                  <rect x="36" y="34.5" width="8" height="4" rx="1" fill="#888888" stroke="#888888" strokeWidth="1"/>
                  <path d="M 8 13 L 8 20 L 10 20" stroke="#333333" strokeWidth="2" fill="none" opacity="0.8"/>
                </svg>

                {/* Dark mode theme */}
                <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <rect x="0" y="0" width="48" height="48" fill="#3a3a3a"/>
                  <rect x="4" y="6" width="42" height="7" rx="2" fill="#2a2a2a" opacity="0.8"/>
                  <rect x="6" y="7.5" width="8" height="4" rx="1" fill="#333333" stroke="#cccccc" strokeWidth="1"/>
                  <rect x="16" y="7.5" width="8" height="4" rx="1" fill="#333333" stroke="#cccccc" strokeWidth="1"/>
                  <rect x="26" y="7.5" width="8" height="4" rx="1" fill="#333333" stroke="#cccccc" strokeWidth="1"/>
                  <rect x="36" y="7.5" width="8" height="4" rx="1" fill="#333333" stroke="#cccccc" strokeWidth="1"/>
                  <rect x="10" y="16" width="32" height="7" rx="2" fill="#2a2a2a" opacity="0.8"/>
                  <rect x="12" y="17.5" width="8" height="4" rx="1" fill="#333333" stroke="#cccccc" strokeWidth="1"/>
                  <rect x="22" y="17.5" width="8" height="4" rx="1" fill="#333333" stroke="#cccccc" strokeWidth="1"/>
                  <rect x="32" y="17.5" width="8" height="4" rx="1" fill="#333333" stroke="#cccccc" strokeWidth="1"/>
                  <rect x="10" y="23" width="32" height="7" rx="2" fill="#2a2a2a" opacity="0.8"/>
                  <rect x="12" y="24.5" width="8" height="4" rx="1" fill="#333333" stroke="#cccccc" strokeWidth="1"/>
                  <rect x="22" y="24.5" width="8" height="4" rx="1" fill="#333333" stroke="#cccccc" strokeWidth="1"/>
                  <rect x="32" y="24.5" width="8" height="4" rx="1" fill="#333333" stroke="#cccccc" strokeWidth="1"/>
                  <rect x="4" y="33" width="42" height="7" rx="2" fill="#2a2a2a" opacity="0.8"/>
                  <rect x="6" y="34.5" width="8" height="4" rx="1" fill="#333333" stroke="#cccccc" strokeWidth="1"/>
                  <rect x="16" y="34.5" width="8" height="4" rx="1" fill="#333333" stroke="#cccccc" strokeWidth="1"/>
                  <rect x="26" y="34.5" width="8" height="4" rx="1" fill="#333333" stroke="#cccccc" strokeWidth="1"/>
                  <rect x="36" y="34.5" width="8" height="4" rx="1" fill="#333333" stroke="#cccccc" strokeWidth="1"/>
                  <path d="M 8 13 L 8 20 L 10 20" stroke="#cccccc" strokeWidth="2" fill="none" opacity="0.5"/>
                </svg>
              </div>
              <h3>Customizable Themes</h3>
              <p>Dark mode and multiple color schemes</p>
            </div>
            <div className="feature">
              <div className="feature-icon" style={{ display: 'flex', flexDirection: 'column', gap: '14px', alignItems: 'center' }}>
                {/* Row 1: 3 users with tables */}
                <div style={{ display: 'flex', gap: '16px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                    <div style={{ fontSize: '32px', filter: 'hue-rotate(200deg)' }}>👤</div>
                    <svg width="38" height="32" viewBox="0 0 38 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <rect x="1" y="1" width="33" height="8" rx="1.2" fill="currentColor" opacity="0.3"/>
                      <rect x="2" y="2" width="7" height="6" rx="0.8" fill="white" opacity="0.9"/>
                      <rect x="10" y="2" width="7" height="6" rx="0.8" fill="white" opacity="0.9"/>
                      <rect x="18" y="2" width="7" height="6" rx="0.8" fill="white" opacity="0.9"/>
                      <rect x="26" y="2" width="7" height="6" rx="0.8" fill="white" opacity="0.9"/>

                      <rect x="1" y="9" width="33" height="8" rx="1.2" fill="currentColor" opacity="0.3"/>
                      <rect x="2" y="10" width="7" height="6" rx="0.8" fill="white" opacity="0.9"/>
                      <rect x="10" y="10" width="7" height="6" rx="0.8" fill="white" opacity="0.9"/>
                      <rect x="18" y="10" width="7" height="6" rx="0.8" fill="white" opacity="0.9"/>
                      <rect x="26" y="10" width="7" height="6" rx="0.8" fill="white" opacity="0.9"/>

                      <rect x="1" y="17" width="33" height="8" rx="1.2" fill="currentColor" opacity="0.3"/>
                      <rect x="2" y="18" width="7" height="6" rx="0.8" fill="white" opacity="0.9"/>
                      <rect x="10" y="18" width="7" height="6" rx="0.8" fill="white" opacity="0.9"/>
                      <rect x="18" y="18" width="7" height="6" rx="0.8" fill="white" opacity="0.9"/>
                      <rect x="26" y="18" width="7" height="6" rx="0.8" fill="white" opacity="0.9"/>
                    </svg>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                    <div style={{ fontSize: '32px', filter: 'hue-rotate(90deg)' }}>👤</div>
                    <svg width="38" height="24" viewBox="0 0 38 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <rect x="1" y="1" width="33" height="8" rx="1.2" fill="currentColor" opacity="0.3"/>
                      <rect x="2" y="2" width="7" height="6" rx="0.8" fill="white" opacity="0.9"/>
                      <rect x="10" y="2" width="7" height="6" rx="0.8" fill="white" opacity="0.9"/>
                      <rect x="18" y="2" width="7" height="6" rx="0.8" fill="white" opacity="0.9"/>
                      <rect x="26" y="2" width="7" height="6" rx="0.8" fill="white" opacity="0.9"/>

                      <rect x="1" y="9" width="33" height="8" rx="1.2" fill="currentColor" opacity="0.3"/>
                      <rect x="2" y="10" width="7" height="6" rx="0.8" fill="white" opacity="0.9"/>
                      <rect x="10" y="10" width="7" height="6" rx="0.8" fill="white" opacity="0.9"/>
                      <rect x="18" y="10" width="7" height="6" rx="0.8" fill="white" opacity="0.9"/>
                      <rect x="26" y="10" width="7" height="6" rx="0.8" fill="white" opacity="0.9"/>
                    </svg>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                    <div style={{ fontSize: '32px', filter: 'hue-rotate(320deg)' }}>👤</div>
                    <svg width="38" height="32" viewBox="0 0 38 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <rect x="1" y="1" width="33" height="8" rx="1.2" fill="currentColor" opacity="0.3"/>
                      <rect x="2" y="2" width="7" height="6" rx="0.8" fill="white" opacity="0.9"/>
                      <rect x="10" y="2" width="7" height="6" rx="0.8" fill="white" opacity="0.9"/>
                      <rect x="18" y="2" width="7" height="6" rx="0.8" fill="white" opacity="0.9"/>
                      <rect x="26" y="2" width="7" height="6" rx="0.8" fill="white" opacity="0.9"/>

                      <rect x="1" y="9" width="33" height="8" rx="1.2" fill="currentColor" opacity="0.3"/>
                      <rect x="2" y="10" width="7" height="6" rx="0.8" fill="white" opacity="0.9"/>
                      <rect x="10" y="10" width="7" height="6" rx="0.8" fill="white" opacity="0.9"/>
                      <rect x="18" y="10" width="7" height="6" rx="0.8" fill="white" opacity="0.9"/>
                      <rect x="26" y="10" width="7" height="6" rx="0.8" fill="white" opacity="0.9"/>

                      <rect x="1" y="17" width="33" height="8" rx="1.2" fill="currentColor" opacity="0.3"/>
                      <rect x="2" y="18" width="7" height="6" rx="0.8" fill="white" opacity="0.9"/>
                      <rect x="10" y="18" width="7" height="6" rx="0.8" fill="white" opacity="0.9"/>
                      <rect x="18" y="18" width="7" height="6" rx="0.8" fill="white" opacity="0.9"/>
                      <rect x="26" y="18" width="7" height="6" rx="0.8" fill="white" opacity="0.9"/>
                    </svg>
                  </div>
                </div>

                {/* Row 2: 2 users with tables */}
                <div style={{ display: 'flex', gap: '16px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                    <div style={{ fontSize: '32px', filter: 'hue-rotate(45deg)' }}>👤</div>
                    <svg width="38" height="32" viewBox="0 0 38 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <rect x="1" y="1" width="33" height="8" rx="1.2" fill="currentColor" opacity="0.3"/>
                      <rect x="2" y="2" width="7" height="6" rx="0.8" fill="white" opacity="0.9"/>
                      <rect x="10" y="2" width="7" height="6" rx="0.8" fill="white" opacity="0.9"/>
                      <rect x="18" y="2" width="7" height="6" rx="0.8" fill="white" opacity="0.9"/>
                      <rect x="26" y="2" width="7" height="6" rx="0.8" fill="white" opacity="0.9"/>

                      <rect x="1" y="9" width="33" height="8" rx="1.2" fill="currentColor" opacity="0.3"/>
                      <rect x="2" y="10" width="7" height="6" rx="0.8" fill="white" opacity="0.9"/>
                      <rect x="10" y="10" width="7" height="6" rx="0.8" fill="white" opacity="0.9"/>
                      <rect x="18" y="10" width="7" height="6" rx="0.8" fill="white" opacity="0.9"/>
                      <rect x="26" y="10" width="7" height="6" rx="0.8" fill="white" opacity="0.9"/>

                      <rect x="1" y="17" width="33" height="8" rx="1.2" fill="currentColor" opacity="0.3"/>
                      <rect x="2" y="18" width="7" height="6" rx="0.8" fill="white" opacity="0.9"/>
                      <rect x="10" y="18" width="7" height="6" rx="0.8" fill="white" opacity="0.9"/>
                      <rect x="18" y="18" width="7" height="6" rx="0.8" fill="white" opacity="0.9"/>
                      <rect x="26" y="18" width="7" height="6" rx="0.8" fill="white" opacity="0.9"/>
                    </svg>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                    <div style={{ fontSize: '32px', filter: 'hue-rotate(270deg)' }}>👤</div>
                    <svg width="38" height="24" viewBox="0 0 38 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <rect x="1" y="1" width="33" height="8" rx="1.2" fill="currentColor" opacity="0.3"/>
                      <rect x="2" y="2" width="7" height="6" rx="0.8" fill="white" opacity="0.9"/>
                      <rect x="10" y="2" width="7" height="6" rx="0.8" fill="white" opacity="0.9"/>
                      <rect x="18" y="2" width="7" height="6" rx="0.8" fill="white" opacity="0.9"/>
                      <rect x="26" y="2" width="7" height="6" rx="0.8" fill="white" opacity="0.9"/>

                      <rect x="1" y="9" width="33" height="8" rx="1.2" fill="currentColor" opacity="0.3"/>
                      <rect x="2" y="10" width="7" height="6" rx="0.8" fill="white" opacity="0.9"/>
                      <rect x="10" y="10" width="7" height="6" rx="0.8" fill="white" opacity="0.9"/>
                      <rect x="18" y="10" width="7" height="6" rx="0.8" fill="white" opacity="0.9"/>
                      <rect x="26" y="10" width="7" height="6" rx="0.8" fill="white" opacity="0.9"/>
                    </svg>
                  </div>
                </div>
              </div>
              <h3>Multi-user System</h3>
              <p>Collaborate with your team in real-time</p>
            </div>
            <div className="feature">
              <div className="feature-icon" style={{ display: 'flex', alignItems: 'center', gap: '12px', justifyContent: 'center' }}>
                {/* Input data on left */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', fontSize: '20px', opacity: 0.7 }}>
                  <div>📄 Data</div>
                  <div>📊 Info</div>
                  <div>📝 Text</div>
                </div>

                {/* Arrow into AI */}
                <div style={{ fontSize: '24px', opacity: 0.5 }}>→</div>

                {/* Sparkles in the middle */}
                <div style={{ fontSize: '56px' }}>✨</div>

                {/* Three arrows out of AI */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', fontSize: '24px', opacity: 0.5 }}>
                  <div style={{ transform: 'rotate(-25deg)' }}>→</div>
                  <div>→</div>
                  <div style={{ transform: 'rotate(25deg)' }}>→</div>
                </div>

                {/* Output tables on right */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  {/* Simple table */}
                  <svg width="48" height="24" viewBox="0 0 48 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <rect x="2" y="2" width="41" height="8" rx="1.2" fill="currentColor" opacity="0.3"/>
                    <rect x="3" y="3" width="9" height="6" rx="0.8" fill="white" opacity="0.9"/>
                    <rect x="13" y="3" width="9" height="6" rx="0.8" fill="white" opacity="0.9"/>
                    <rect x="23" y="3" width="9" height="6" rx="0.8" fill="white" opacity="0.9"/>
                    <rect x="33" y="3" width="9" height="6" rx="0.8" fill="white" opacity="0.9"/>

                    <rect x="2" y="10" width="41" height="8" rx="1.2" fill="currentColor" opacity="0.3"/>
                    <rect x="3" y="11" width="9" height="6" rx="0.8" fill="white" opacity="0.9"/>
                    <rect x="13" y="11" width="9" height="6" rx="0.8" fill="white" opacity="0.9"/>
                    <rect x="23" y="11" width="9" height="6" rx="0.8" fill="white" opacity="0.9"/>
                    <rect x="33" y="11" width="9" height="6" rx="0.8" fill="white" opacity="0.9"/>
                  </svg>

                  {/* Nested table with subtables */}
                  <svg width="48" height="42" viewBox="0 0 48 42" fill="none" xmlns="http://www.w3.org/2000/svg">
                    {/* Parent row */}
                    <rect x="2" y="2" width="41" height="8" rx="1.2" fill="currentColor" opacity="0.3"/>
                    <rect x="3" y="3" width="9" height="6" rx="0.8" fill="white" opacity="0.9"/>
                    <rect x="13" y="3" width="9" height="6" rx="0.8" fill="white" opacity="0.9"/>
                    <rect x="23" y="3" width="9" height="6" rx="0.8" fill="white" opacity="0.9"/>
                    <rect x="33" y="3" width="9" height="6" rx="0.8" fill="white" opacity="0.9"/>

                    {/* First child row */}
                    <rect x="6" y="12" width="35" height="8" rx="1.2" fill="currentColor" opacity="0.3"/>
                    <rect x="7" y="13" width="9" height="6" rx="0.8" fill="white" opacity="0.8"/>
                    <rect x="17" y="13" width="9" height="6" rx="0.8" fill="white" opacity="0.8"/>
                    <rect x="27" y="13" width="13" height="6" rx="0.8" fill="white" opacity="0.8"/>

                    {/* Second child row */}
                    <rect x="6" y="20" width="35" height="8" rx="1.2" fill="currentColor" opacity="0.3"/>
                    <rect x="7" y="21" width="9" height="6" rx="0.8" fill="white" opacity="0.8"/>
                    <rect x="17" y="21" width="9" height="6" rx="0.8" fill="white" opacity="0.8"/>
                    <rect x="27" y="21" width="13" height="6" rx="0.8" fill="white" opacity="0.8"/>

                    {/* Bottom parent row */}
                    <rect x="2" y="30" width="41" height="8" rx="1.2" fill="currentColor" opacity="0.3"/>
                    <rect x="3" y="31" width="9" height="6" rx="0.8" fill="white" opacity="0.9"/>
                    <rect x="13" y="31" width="9" height="6" rx="0.8" fill="white" opacity="0.9"/>
                    <rect x="23" y="31" width="9" height="6" rx="0.8" fill="white" opacity="0.9"/>
                    <rect x="33" y="31" width="9" height="6" rx="0.8" fill="white" opacity="0.9"/>

                    {/* Connector line */}
                    <path d="M 4 10 L 4 16 L 6 16" stroke="currentColor" strokeWidth="1.5" fill="none" opacity="0.5"/>
                  </svg>

                  {/* Third table - 3 rows */}
                  <svg width="48" height="33" viewBox="0 0 48 33" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <rect x="2" y="2" width="41" height="8" rx="1.2" fill="currentColor" opacity="0.3"/>
                    <rect x="3" y="3" width="9" height="6" rx="0.8" fill="white" opacity="0.9"/>
                    <rect x="13" y="3" width="9" height="6" rx="0.8" fill="white" opacity="0.9"/>
                    <rect x="23" y="3" width="9" height="6" rx="0.8" fill="white" opacity="0.9"/>
                    <rect x="33" y="3" width="9" height="6" rx="0.8" fill="white" opacity="0.9"/>

                    <rect x="2" y="10" width="41" height="8" rx="1.2" fill="currentColor" opacity="0.3"/>
                    <rect x="3" y="11" width="9" height="6" rx="0.8" fill="white" opacity="0.9"/>
                    <rect x="13" y="11" width="9" height="6" rx="0.8" fill="white" opacity="0.9"/>
                    <rect x="23" y="11" width="9" height="6" rx="0.8" fill="white" opacity="0.9"/>
                    <rect x="33" y="11" width="9" height="6" rx="0.8" fill="white" opacity="0.9"/>

                    <rect x="2" y="18" width="41" height="8" rx="1.2" fill="currentColor" opacity="0.3"/>
                    <rect x="3" y="19" width="9" height="6" rx="0.8" fill="white" opacity="0.9"/>
                    <rect x="13" y="19" width="9" height="6" rx="0.8" fill="white" opacity="0.9"/>
                    <rect x="23" y="19" width="9" height="6" rx="0.8" fill="white" opacity="0.9"/>
                    <rect x="33" y="19" width="9" height="6" rx="0.8" fill="white" opacity="0.9"/>
                  </svg>
                </div>
              </div>
              <h3>AI-Powered Table Generation</h3>
              <p>Describe your data and let AI create the perfect table structure</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default LandingPage;
